/**
 * MinimumSafeguards — EU Taxonomy Regulation (EU) 2020/852, Art. 18.
 *
 * An activity is only Taxonomy-aligned when carried out in alignment with the
 * OECD Guidelines for Multinational Enterprises and the UN Guiding Principles
 * on Business and Human Rights. This page reads the verdict derived from five
 * CSDDD due-diligence signals and lets a compliance manager file a new
 * assessment. Chrome comes entirely from EsgKit; API contract lives in
 * backend/routes/min_safeguards_routes.py + services/min_safeguards_service.py.
 */
import { useCallback, useEffect, useState } from "react";
import {
  ShieldCheck, ShieldAlert, ShieldQuestion, CheckCircle2, XCircle, Minus,
} from "lucide-react";
import {
  Page, PageHeader, Card, Overline, Badge, Data, Btn, DataTable, EmptyState,
  ErrorBanner, Skeleton, field, Label, apiFetch, C, MONO, SANS, HEAD,
} from "../../components/esg/EsgKit";
import { useEntities } from "../../hooks/useEntities";

// ─── Signal vocabulary (order = Art. 18 expectations) ──────────────────────────
const SIGNALS = [
  ["hrdd_policy_in_place", "HRDD policy in place"],
  ["grievance_mechanism_active", "Grievance mechanism active"],
  ["no_unremediated_critical_impacts", "No unremediated critical impacts"],
  ["oecd_alignment_confirmed", "OECD Guidelines alignment"],
  ["ungp_alignment_confirmed", "UNGP alignment"],
];
const LABELS = Object.fromEntries(SIGNALS);

const BASE = "/api/taxonomy/safeguards";

// tri-state select state is held as strings; convert on submit.
const toBool = (v) => (v === "null" ? null : v === "true");
const fromBool = (b) => (b === null || b === undefined ? "null" : b ? "true" : "false");

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toISOString().slice(0, 10);
};

// tri-state cell for the history table: icon + full-label tooltip.
function SignalCell({ value, label }) {
  const meta =
    value === true
      ? { Icon: CheckCircle2, c: C.primary, state: "Confirmed" }
      : value === false
      ? { Icon: XCircle, c: C.destructive, state: "Failed" }
      : { Icon: Minus, c: C.neutral, state: "Insufficient evidence" };
  return (
    <span
      title={`${label} — ${meta.state}`}
      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}
    >
      <meta.Icon size={15} strokeWidth={1.5} color={meta.c} />
    </span>
  );
}

export default function MinimumSafeguards() {
  const { entities, entityId, setEntityId } = useEntities("min_safeguards_entity_id");

  const [verdict, setVerdict] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const blankForm = () => ({
    ...Object.fromEntries(SIGNALS.map(([k]) => [k, "null"])),
    evidence_ids: "",
    notes: "",
  });
  const [form, setForm] = useState(blankForm);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    setError("");
    const q = `?entity_id=${encodeURIComponent(entityId)}`;
    const [vRes, aRes] = await Promise.all([
      // verdict may 404/error when no assessment exists — that is "no verdict
      // yet", not a page-level failure.
      apiFetch(`${BASE}/verdict${q}`).catch(() => null),
      apiFetch(`${BASE}/assessments${q}`).catch((e) => {
        setError(e.message || "Failed to load assessments");
        return { assessments: [] };
      }),
    ]);
    setVerdict(vRes);
    setAssessments(aRes?.assessments || []);
    setLoading(false);
  }, [entityId]);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!entityId) return;
    setSubmitting(true);
    setError("");
    const body = { entity_id: entityId };
    for (const [k] of SIGNALS) body[k] = toBool(form[k]); // null stays null
    body.evidence_ids = form.evidence_ids
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    body.notes = form.notes;
    try {
      await apiFetch(`${BASE}/assessments`, { method: "POST", body });
      setForm(blankForm());
      await load();
    } catch (err) {
      setError(err.message || "Failed to file assessment");
    } finally {
      setSubmitting(false);
    }
  };

  const header = (
    <PageHeader
      overline="EU Taxonomy · Art. 18"
      OverlineIcon={ShieldCheck}
      title="Minimum Safeguards"
      subtitle="OECD Guidelines / UNGP alignment verdict derived from CSDDD due-diligence signals — required for any Taxonomy-aligned activity."
      entities={entities}
      entityId={entityId}
      onEntityChange={setEntityId}
      onRefresh={load}
      loading={loading}
    />
  );

  if (!entityId) {
    return (
      <Page>
        {header}
        <Card>
          <EmptyState
            Icon={ShieldQuestion}
            title="Select a reporting entity"
            body="Minimum-safeguards verdicts are scoped to a reporting entity. Choose one to view its assessment and file a new one."
          />
        </Card>
      </Page>
    );
  }

  return (
    <Page>
      {header}
      <ErrorBanner message={error} onDismiss={() => setError("")} />

      {loading && !verdict && assessments.length === 0 ? (
        <div style={{ display: "grid", gap: 16 }}>
          <Skeleton h={140} />
          <Skeleton h={220} />
          <Skeleton h={180} />
        </div>
      ) : (
        <div style={{ display: "grid", gap: 16 }}>
          <VerdictHero verdict={verdict} assessments={assessments} />
          <NewAssessment
            form={form}
            setField={setField}
            onSubmit={submit}
            submitting={submitting}
          />
          <History assessments={assessments} />
        </div>
      )}
    </Page>
  );
}

// ─── Verdict hero ───────────────────────────────────────────────────────────────
function VerdictHero({ verdict, assessments }) {
  const hasAssessment = verdict && verdict.assessment_id;
  if (!hasAssessment) {
    return (
      <Card>
        <EmptyState
          Icon={ShieldQuestion}
          title="No safeguards assessment yet"
          body="File a minimum-safeguards assessment below to derive an OECD/UNGP alignment verdict for this entity."
        />
      </Card>
    );
  }

  const v = verdict.verdict;
  const conf =
    v === "compliant"
      ? { c: C.primary, Icon: ShieldCheck, label: "PASSES" }
      : v === "non_compliant"
      ? { c: C.destructive, Icon: ShieldAlert, label: "FAILS" }
      : { c: C.accent, Icon: ShieldQuestion, label: "INSUFFICIENT EVIDENCE" };

  // date comes from the latest assessment (verdict payload carries none).
  const latest = assessments[0];
  const chips = [
    ...(verdict.failed_flags || []).map((f) => ({ f, tone: C.destructive })),
    ...(verdict.missing_flags || []).map((f) => ({ f, tone: C.accent })),
  ];

  return (
    <Card accent={conf.c}>
      <Overline Icon={conf.Icon} color={conf.c}>
        Minimum-safeguards verdict
      </Overline>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <conf.Icon size={32} strokeWidth={1.5} color={conf.c} />
        <span
          style={{
            fontFamily: HEAD,
            fontSize: 28,
            fontWeight: 700,
            letterSpacing: "-0.01em",
            color: conf.c,
          }}
        >
          {conf.label}
        </span>
      </div>
      <div style={{ marginTop: 10, fontFamily: SANS, fontSize: 12, color: C.body }}>
        Assessed <Data color={C.body}>{fmtDate(latest?.assessed_at)}</Data>
      </div>
      {chips.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div
            style={{
              fontFamily: SANS,
              fontSize: 11,
              color: C.body,
              marginBottom: 8,
            }}
          >
            {verdict.verdict === "non_compliant" ? "Failed signals" : "Signals blocking a pass"}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {chips.map(({ f, tone }) => (
              <Badge
                key={f}
                c={tone}
                Icon={tone === C.destructive ? XCircle : Minus}
                label={LABELS[f] || f}
              />
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

// ─── New assessment form ────────────────────────────────────────────────────────
const OPTIONS = [
  ["true", "Confirmed"],
  ["false", "Failed"],
  ["null", "Insufficient evidence"],
];

function NewAssessment({ form, setField, onSubmit, submitting }) {
  return (
    <Card>
      <Overline Icon={ShieldCheck}>New assessment</Overline>
      <form onSubmit={onSubmit}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 14,
          }}
        >
          {SIGNALS.map(([k, label]) => (
            <div key={k}>
              <Label>{label}</Label>
              <select
                value={form[k]}
                onChange={(e) => setField(k, e.target.value)}
                style={field}
              >
                {OPTIONS.map(([val, txt]) => (
                  <option key={val} value={val}>
                    {txt}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 14 }}>
          <Label>Evidence IDs (comma-separated)</Label>
          <input
            type="text"
            value={form.evidence_ids}
            onChange={(e) => setField("evidence_ids", e.target.value)}
            placeholder="ev_001, ev_002"
            style={field}
          />
        </div>

        <div style={{ marginTop: 14 }}>
          <Label>Notes</Label>
          <textarea
            value={form.notes}
            onChange={(e) => setField("notes", e.target.value)}
            rows={3}
            placeholder="Assessment rationale, caveats, links to CSDDD due-diligence records…"
            style={{ ...field, resize: "vertical", minHeight: 68 }}
          />
        </div>

        <div style={{ marginTop: 16 }}>
          <Btn type="submit" Icon={ShieldCheck} disabled={submitting}>
            {submitting ? "Filing…" : "File assessment"}
          </Btn>
        </div>
      </form>
    </Card>
  );
}

// ─── Assessment history ─────────────────────────────────────────────────────────
function History({ assessments }) {
  const signalCol = (k, label) => ({
    key: k,
    label,
    align: "center",
    render: (row) => <SignalCell value={row[k]} label={label} />,
  });

  const shortLabels = {
    hrdd_policy_in_place: "HRDD",
    grievance_mechanism_active: "Grievance",
    no_unremediated_critical_impacts: "No impacts",
    oecd_alignment_confirmed: "OECD",
    ungp_alignment_confirmed: "UNGP",
  };

  const columns = [
    {
      key: "assessed_at",
      label: "Assessed",
      render: (row) => <Data color={C.body}>{fmtDate(row.assessed_at)}</Data>,
    },
    ...SIGNALS.map(([k, label]) => ({
      ...signalCol(k, shortLabels[k] || label),
      label: (
        <span title={label} style={{ fontFamily: SANS }}>
          {shortLabels[k] || label}
        </span>
      ),
    })),
    {
      key: "notes",
      label: "Notes",
      render: (row) =>
        row.notes ? (
          <span
            title={row.notes}
            style={{
              fontFamily: MONO,
              fontSize: 12,
              color: C.body,
              display: "inline-block",
              maxWidth: 220,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              verticalAlign: "middle",
            }}
          >
            {row.notes}
          </span>
        ) : (
          <Data color={C.muted}>—</Data>
        ),
    },
  ];

  return (
    <Card>
      <Overline Icon={ShieldCheck}>Assessment history</Overline>
      {assessments.length === 0 ? (
        <EmptyState
          Icon={ShieldQuestion}
          title="No assessments filed"
          body="Filed assessments appear here with their five signal states, most recent first."
        />
      ) : (
        <DataTable
          columns={columns}
          rows={assessments}
          keyField="assessment_id"
          emptyLabel="No assessments filed"
        />
      )}
    </Card>
  );
}
